/**
 * Auto-recovery para chunk loading errors em deploys.
 *
 * Cenário: usuário tem a SPA aberta há tempo. Deploy substitui os chunks.
 * Próximo lazy() import pede um chunk que não existe mais → promise rejeita
 * com mensagem específica por browser. Esse helper detecta e recarrega a
 * página uma vez para forçar o navegador a buscar o index.html novo.
 *
 * Proteção contra loop: grava timestamp da tentativa em sessionStorage; se
 * outro chunk error chega dentro do cooldown, NÃO recarrega de novo —
 * deixa o ErrorBoundary mostrar o fallback ao usuário.
 */

const CHUNK_ERROR_PATTERNS: readonly string[] = [
  'Importing a module script failed', // Safari
  'Failed to fetch dynamically imported module', // Chrome/Edge
  'error loading dynamically imported module', // Firefox
  'Loading chunk', // webpack-style
  'Loading CSS chunk', // webpack-style CSS
  'Unable to preload CSS', // Vite preload
];

const RELOAD_FLAG_KEY = 'chunk_reload_attempted_at';
const RELOAD_COOLDOWN_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export interface ReloadDeps {
  now: () => number;
  getFlag: () => string | null;
  setFlag: (value: string) => void;
  reload: () => void;
}

/**
 * Retorna `true` se o reload foi disparado, `false` se foi pulado
 * (não é chunk error, ou já recarregou dentro do cooldown).
 */
export function maybeReloadOnChunkError(error: unknown, deps: ReloadDeps): boolean {
  if (!isChunkLoadError(error)) return false;

  const now = deps.now();
  const lastAttempt = Number(deps.getFlag() ?? 0);

  if (now - lastAttempt < RELOAD_COOLDOWN_MS) {
    return false;
  }

  deps.setFlag(String(now));
  deps.reload();
  return true;
}

/**
 * Wrapper para uso em produção. Em SSR/teste sem window, vira no-op.
 */
export function tryReloadOnChunkError(error: unknown): boolean {
  if (typeof window === 'undefined') return false;
  return maybeReloadOnChunkError(error, {
    now: () => Date.now(),
    getFlag: () => window.sessionStorage.getItem(RELOAD_FLAG_KEY),
    setFlag: (value) => window.sessionStorage.setItem(RELOAD_FLAG_KEY, value),
    reload: () => window.location.reload(),
  });
}
